import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axiosInstance from '../axiosInstance';
import llStorage from '../llStorage';
import './WheelPopup.css';

// Example prize list (7 items). Each item: { label, isWin: bool, stars: 0-5, wheelEntryId: null }
const DEFAULT_PRIZES = [
  { label: 'Zkuste to znovu', isWin: false, stars: 0, resultText: "", tryAgain: true, wheelEntryId: null },
  { label: 'Děkuji', isWin: true, stars: 1, resultText: "Tentokrát jste vyhráli pouhé Děkuji. Věříme, že i to Vás zahřeje u srdce.", tryAgain: false, wheelEntryId: null  },
  { label: 'Malý dárek', isWin: true, stars: 2, resultText: "Sada našich klíčenek k Vašim službám.", tryAgain: false, wheelEntryId: null },
  { label: 'Sleva 10%', isWin: true, stars: 3, resultText: "Slevový kupón na nákup v našem obchodě.", tryAgain: false, wheelEntryId: null  },    
  { label: 'Sleva 25%', isWin: true, stars: 4, resultText: "Slevový kupón na nákup v našem obchodě.", tryAgain: false, wheelEntryId: null  },
  { label: 'Velký dárek', isWin: true, stars: 5, resultText: "iPhone od LostLink.store je Váš", tryAgain: false, wheelEntryId: null  },  
];

const MODE_DEMO = 'demo';
const MODE_ELIGIBLE = 'eligible';
const MODE_SPONSOR_DEMO = 'sponsorDemo';

function WheelPopup({
  prizes = DEFAULT_PRIZES,
  onClose = () => {},
  initialSpins = 3,
  mode,
  eligibleSpins,
  sponsorCampaignId
}) {
  const [prizesData, setPrizesData] = useState(prizes);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [resultIndex, setResultIndex] = useState(null);
  const [highlightWin, setHighlightWin] = useState(false);
  const wheelRef = useRef(null);
  const [wheelMode, setWheelMode] = useState(mode || MODE_DEMO);

  // spins tracking: `spinsTotal` (K zatoceni) and `spinsLeft` (Zbyva)
  const [spinsTotal, setSpinsTotal] = useState(initialSpins);
  const [spinsLeft, setSpinsLeft] = useState(initialSpins);

  const modeLabel =
    wheelMode === MODE_ELIGIBLE
      ? 'Režim: Ostrý'
      : wheelMode === MODE_SPONSOR_DEMO
        ? 'Režim: Sponsor demo'
        : 'Režim: Demo - toto je pouze ukázka a neposkytuje skutečné výhry!!!';

  const effectivePrizes = (prizesData && prizesData.length) ? prizesData : prizes;
  const n = effectivePrizes.length;
  const segmentAngle = 360 / n;

  // Record the spin result to the backend (eligible mode only)
  const recordSpinResult = async (prize) => {
    if (!prize?.wheelEntryId || !prize?.label) {
      return;
    }
    try {
      await axiosInstance.post('/wheelcampaigns/record-spin', {
        wheelEntryId: prize.wheelEntryId,
        resultLabel: prize.label,
        stars: prize.stars
      });
    } catch (err) {
      console.error('Failed to record spin result:', err);
    }
  };

  // Start a spin; pick random prize index and animate rotation to land on it
  const spin = () => {
    if (spinning) return;
    if (spinsLeft <= 0) return; // no spins left
    setSpinning(true);
    setResultIndex(null);

    const spins = 6; // full rotations for animation
    // Pick a random final angle (so wheel lands visually at a random spot)
    const randomAngle = Math.random() * 360; // 0..360
    const newRotation = spins * 360 + randomAngle;

    // Capture current rotation so we can compute final landing index deterministically
    const currentRotation = rotation || 0;
    const finalRotation = (currentRotation + newRotation) % 360;

    // Apply rotation
    setRotation((prev) => prev + newRotation);

    // Total animation duration (ms)
    const duration = 4000;
    // After animation ends, compute which segment actually landed under the pointer
    setTimeout(() => {
      // Compute the wheel-coordinate angle that is currently at the top pointer.
      // Solve for alpha where (alpha + finalRotation) %360 == -90 (top in screen coords).
      // So alpha = -90 - finalRotation (mod 360).
      const angleAtTopWheel = (((-90 - finalRotation) % 360) + 360) % 360;
      // Map angle to nearest segment center using half-segment offset, then floor to bucket.
      const landedIndex = Math.floor((angleAtTopWheel + segmentAngle / 2) / segmentAngle) % n;
      setResultIndex(landedIndex);
      // decrement spins unless the prize grants a tryAgain
      const prize = effectivePrizes[landedIndex];
      if (!(prize && prize.tryAgain)) {
        setSpinsLeft((prev) => Math.max(0, prev - 1));
      }
      setSpinning(false);
      // highlight pointer if prize is a win
      if (prize && prize.isWin) {
        // small delay so the wheel settles visually
        setTimeout(() => setHighlightWin(true), 150);
        // remove highlight after 3.5s
        setTimeout(() => setHighlightWin(false), 3650);
      }

      // If in eligible mode, send the spin result to the backend
      if (wheelMode === MODE_ELIGIBLE && prize && prize.isWin && prize.wheelEntryId) {
        recordSpinResult(prize);
      }
    }, duration + 50);
  };

  // Close on Escape and cleanup
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const buildSponsorDemoPrizes = (entries) => {
    const winTemplates = DEFAULT_PRIZES.filter((p) => p.isWin);
    const totalSlots = Math.max(DEFAULT_PRIZES.length, (entries?.length || 0) + 1);
    const slots = [];

    slots.push({ label: 'Zkuste to znovu', isWin: false, stars: 0, resultText: "", tryAgain: true, wheelEntryId: null });

    (entries || []).forEach((entry, index) => {
      const template = winTemplates[index % winTemplates.length] || winTemplates[0];
      slots.push({
        label: entry?.wheelTextShort || entry?.priceText || template.label,
        isWin: true,
        stars: template.stars,
        resultText: entry?.priceText || template.resultText || '',
        tryAgain: false,
        wheelEntryId: entry?._id || null
      });
    });

    while (slots.length < totalSlots) {
      const filler = DEFAULT_PRIZES[slots.length % DEFAULT_PRIZES.length];
      slots.push({ ...filler });
    }

    return slots.slice(0, totalSlots);
  };

  useEffect(() => {
    setPrizesData(prizes);
  }, [prizes]);

  useEffect(() => {
    let isMounted = true;
    const hasExplicitMode = !!mode;
    const hasExplicitEligibleSpins = typeof eligibleSpins === 'number';

    const resolveMode = async () => {
      if (hasExplicitMode) {
        if (isMounted) setWheelMode(mode);
        // Set spins from eligibleSpins prop if provided
        if (hasExplicitEligibleSpins && isMounted) {
          setSpinsTotal(eligibleSpins);
          setSpinsLeft(eligibleSpins);
        }
        return;
      }

      if (hasExplicitEligibleSpins) {
        const resolved = eligibleSpins > 0 ? MODE_ELIGIBLE : MODE_DEMO;
        if (isMounted) setWheelMode(resolved);
        if (eligibleSpins > 0 && isMounted) {
          setSpinsTotal(eligibleSpins);
          setSpinsLeft(eligibleSpins);
        }
        return;
      }

      const token = llStorage.getItem('token');
      if (!token) {
        if (isMounted) setWheelMode(MODE_DEMO);
        return;
      }

      try {
        const res = await axiosInstance.get('/users/me/itemstats');
        if (!isMounted) return;
        const spins = Number(res.data?.foundItemsEligibleSpin || 0);
        if (spins > 0) {
          setWheelMode(MODE_ELIGIBLE);
          setSpinsTotal(spins);
          setSpinsLeft(spins);
        } else {
          setWheelMode(MODE_DEMO);
        }
      } catch (err) {
        console.error('Failed to load user stats for wheel:', err);
        if (isMounted) setWheelMode(MODE_DEMO);
      }
    };

    resolveMode();
    return () => { isMounted = false; };
  }, [mode, eligibleSpins]);

  useEffect(() => {
    let isMounted = true;

    const setDemoPrizes = () => {
      if (isMounted) setPrizesData(prizes);
    };

    const loadEligiblePrizes = async () => {
      try {
        const res = await axiosInstance.get('/wheelcampaigns/prizes');
        if (!isMounted) return;
        if (Array.isArray(res.data) && res.data.length > 0) {
          setPrizesData(res.data);
        } else {
          setDemoPrizes();
        }
      } catch (err) {
        console.error('Failed to load wheel prizes:', err);
        setDemoPrizes();
      }
    };

    const loadSponsorDemoPrizes = async () => {
      if (!sponsorCampaignId) {
        setDemoPrizes();
        return;
      }
      try {
        const res = await axiosInstance.get(`/wheelcampaigns/${sponsorCampaignId}/entries`);
        if (!isMounted) return;
        const entries = Array.isArray(res.data) ? res.data : [];
        setPrizesData(buildSponsorDemoPrizes(entries));
      } catch (err) {
        console.error('Failed to load sponsor wheel prizes:', err);
        setDemoPrizes();
      }
    };

    if (wheelMode === MODE_ELIGIBLE) {
      loadEligiblePrizes();
    } else if (wheelMode === MODE_SPONSOR_DEMO) {
      loadSponsorDemoPrizes();
    } else {
      setDemoPrizes();
    }

    return () => { isMounted = false; };
  }, [wheelMode, sponsorCampaignId, prizes]);

  useEffect(() => {
    if (wheelMode !== MODE_ELIGIBLE) {
      setSpinsTotal(initialSpins);
      setSpinsLeft(initialSpins);
    }
  }, [wheelMode, initialSpins]);

  const onOverlayClick = () => {
    if (!spinning) onClose();
  };

  const stopPropagation = (e) => e.stopPropagation();

  // Helper to render stars
  const renderStars = (count) => {
    const stars = [];
    for (let i = 0; i < Math.min(5, Math.max(0, count)); i++) {
      stars.push(<span key={i} className="star">★</span>);
    }
    return <span className="stars">{stars}</span>;
  };

  // Build SVG segments as paths
  const renderSegments = () => {
    const radius = 160; // visual radius
    const cx = 0;
    const cy = 0;

  const paths = [];
  const labels = [];
  const defs = [];
  // helper: mix two hex colors (weight 0..1 towards color2)
  const mixHex = (h1, h2, weight = 0.5) => {
    const toRgb = (h) => {
      const hex = h.replace('#','');
      return [parseInt(hex.substring(0,2),16), parseInt(hex.substring(2,4),16), parseInt(hex.substring(4,6),16)];
    };
    const a = toRgb(h1), b = toRgb(h2);
    const mix = [
      Math.round(a[0] + (b[0]-a[0]) * weight),
      Math.round(a[1] + (b[1]-a[1]) * weight),
      Math.round(a[2] + (b[2]-a[2]) * weight),
    ];
    return `rgb(${mix[0]},${mix[1]},${mix[2]})`;
  };
  for (let i = 0; i < n; i++) {
      const startAngle = (i * segmentAngle - segmentAngle / 2) * (Math.PI / 180);
      const endAngle = ((i + 1) * segmentAngle - segmentAngle / 2) * (Math.PI / 180);
      const x1 = cx + radius * Math.cos(startAngle);
      const y1 = cy + radius * Math.sin(startAngle);
      const x2 = cx + radius * Math.cos(endAngle);
      const y2 = cy + radius * Math.sin(endAngle);

      const largeArc = segmentAngle > 180 ? 1 : 0;
      const path = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;

  const prize = effectivePrizes[i];
  const isHighlight = resultIndex === i && resultIndex !== null;
  const className = `${prize && prize.isWin ? 'segment win' : 'segment lose'}${isHighlight ? ' highlight' : ''}`;

      

  // place plain rotated text at the wedge midpoint (angle = segmentAngle/2)
  const midAngleDeg = i * segmentAngle;// + segmentAngle / 2;
  const midAngleRad = midAngleDeg * (Math.PI / 180);
  // font and radial placement that accounts for text height so it doesn't clip at rim
  // move labels further toward center to match requested placement (see screenshot)
  const labelFontSize = 10; // px (SVG units) — slightly smaller for fit
  const baseLabelRadiusFactor = 0.7; // base distance from center (0..1) — nearer the center for desired look
  // subtract half text-height plus a small padding so the glyphs don't touch the rim
  const adjustedLabelRadiusFactor = baseLabelRadiusFactor - (labelFontSize / 2 + 6) / radius;
  const labelXPos = cx + radius * adjustedLabelRadiusFactor * Math.cos(midAngleRad);
  const labelYPos = cy + radius * adjustedLabelRadiusFactor * Math.sin(midAngleRad);
  // rotate text so it points inward along the radial; keep upright
  let angleToCenter = (midAngleDeg + 180) % 360;
  if (angleToCenter > 90 && angleToCenter < 270) angleToCenter = (angleToCenter + 180) % 360;

      // choose a colorful palette for the wheel (cycle if needed)
      const palette = ['#e94b35', '#f6c132', '#6ad1d0', '#7b61ff', '#ff6b6b', '#3fd07a', '#ff8c42', '#4aa3ff'];
      const color = palette[i % palette.length];
      // create a radial gradient for this segment (lighter in center)
      const gradId = `segGrad-${i}`;
      const light = mixHex(color, '#ffffff', 0.6);
      defs.push(
        <radialGradient key={`g-${i}`} id={gradId} gradientUnits="userSpaceOnUse" cx="0" cy="0" r={radius + 20}>
          <stop offset="0%" stopColor={light} stopOpacity="1" />
          <stop offset="70%" stopColor={color} stopOpacity="1" />
        </radialGradient>
      );
      // push path first with gradient fill
      paths.push(
        <path key={`p-${i}`} d={path} className={className} style={{ fill: `url(#${gradId})`, stroke: '#ffffff22', strokeWidth: 1 }} />
      );
      // push label to separate labels array so we can render them on top
      labels.push(
        <text
          key={`t-${i}`}
          className="segment-label"
          x={labelXPos.toFixed(2)}
          y={labelYPos.toFixed(2)}
          fill="#fff"
          fontSize={labelFontSize}
          textAnchor="middle"
          dominantBaseline="middle"
          transform={`rotate(${angleToCenter} ${labelXPos.toFixed(2)} ${labelYPos.toFixed(2)})`}
        >
          {prize.isWin ? prize.label : '—'}
        </text>
      );
    }
    // decorative rim, bulbs and hub rendered under labels but above segments as needed
    // build rim bulbs
    const bulbs = [];
    const bulbCount = Math.max(12, n * 2);
    const rimRadius = radius + 18;
    for (let b = 0; b < bulbCount; b++) {
      const a = (b * (360 / bulbCount)) * (Math.PI / 180);
      const bx = rimRadius * Math.cos(a);
      const by = rimRadius * Math.sin(a);
      bulbs.push(<circle key={`bulb-${b}`} cx={bx.toFixed(2)} cy={by.toFixed(2)} r={6} className="rim-bulb" />);
    }

    const rim = (
      <g key="rim-decor" className="rim-decor">
        <circle cx={0} cy={0} r={radius + 14} className="wheel-rim" />
        {bulbs}
        <circle cx={0} cy={0} r={36} className="wheel-hub" />
      </g>
    );

    // include gradient defs, then render paths, rim decor, then labels so labels are painted on top
    return [
      <defs key="defs">{defs}</defs>,
      <g key="segments-group" className="segments-group">{paths}</g>,
      rim,
      <g key="labels-group" className="labels-group">{labels}</g>,
    ];
  };

  const popupContent = (
    <div className="wheel-modal" onClick={onOverlayClick} role="dialog" aria-modal="true">
      <div className="wheel-content" onClick={stopPropagation}>
        <div className="wheel-mode" style={{ textAlign: 'center', marginBottom: 6, fontSize: 12, opacity: 0.8 }}>
          {modeLabel}
        </div>
        <button className="close-button" aria-label="Close" onClick={() => { if (!spinning) onClose(); }}>&times;</button>
        <div className="wheel-container">
          <div className={`wheel-pointer ${highlightWin ? 'win' : ''}`}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <circle cx="12" cy="12" r="11" fill="#ffdd57" />
              <text x="12" y="16" textAnchor="middle" fontSize="14" fontWeight="700" fill="#b65b00">★</text>
            </svg>
          </div>
          {/* Pointer tab that overlaps the wheel and displays the landed prize */}
          <div className="pointer-tab">
            {resultIndex !== null ? (
              <div className="tab-content">
                <div className="tab-label">{effectivePrizes[resultIndex]?.label}</div>
                <div className="tab-stars">{effectivePrizes[resultIndex]?.isWin ? renderStars(effectivePrizes[resultIndex].stars) : null}</div>
              </div>
            ) : (
              <div className="tab-content tab-empty">&nbsp;</div>
            )}
          </div>
          <svg
            viewBox="-200 -200 400 400"
            className="wheel-svg"
            ref={wheelRef}
            style={{ transform: `rotate(${rotation}deg)`, transition: spinning ? 'transform 4s cubic-bezier(.17,.67,.33,1)' : 'transform 0.6s ease-out' }}
          >
            {renderSegments()}
          </svg>
        </div>

        <div className="wheel-controls">
          <div className="spins-info" style={{ marginBottom: 8, textAlign: 'center' }}>
            <small>K zatočení: {spinsTotal} &nbsp;|&nbsp; Zbývá: {spinsLeft}</small>
          </div>
          <button className="spin-button" onClick={spin} disabled={spinning || spinsLeft <= 0}>
            {spinning ? 'Točí se...' : 'Zatočit'}
          </button>
        </div>

        {resultIndex !== null && (() => {
          const prize = effectivePrizes[resultIndex];
          if (!prize) return <div className="wheel-result"><strong>Result:</strong> {"Unknown result"}</div>;
          return (
            <div className="wheel-result">
              <strong></strong> {prize.label} {renderStars(prize.stars) }
              <div style={{ marginTop: 8 }}>{prize.resultText || ''}</div>
              {/* If prize.tryAgain is true we intentionally hide the explicit "Zkusit znovu" button
                  Users should just press the main Spin button again; tryAgain spins are not decremented */}
            </div>
          );
        })()}
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return popupContent;
  }

  return createPortal(popupContent, document.body);
}

export default WheelPopup;
